import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Search, RefreshCw } from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from '../ui/simple-toaster';
import { InviteDriverModal, DriverDetailModal, EditDriverModal } from './DriverModalsAdmin';
import { AdminSortTh, DriverMobileCard, DriverTableRow } from './drivers/DriverSubComponents';

const DriversPanel = ({ drivers, onRefresh }) => {
  const [showInvite, setShowInvite] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [inviteResult, setInviteResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [driverState, setDriverState] = useState(null);
  const [driverStates, setDriverStates] = useState({});
  const [statesLoading, setStatesLoading] = useState(false);
  const [sortField, setSortField] = useState('hours_avail');
  const [sortDir, setSortDir] = useState('asc');
  
  const [inviteForm, setInviteForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    license_number: '',
    license_state: 'NSW',
    license_card_number: '',
    date_of_birth: '',
    phone: ''
  });
  
  const [editForm, setEditForm] = useState({
    license_number: '',
    license_state: '',
    license_card_number: '',
    date_of_birth: '',
    base_address: '',
    status: ''
  });

  // Filter drivers by search and sort
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filteredDrivers = useMemo(() => {
    let list = drivers.filter(d => {
      const name = `${d.users?.first_name || ''} ${d.users?.last_name || ''}`.toLowerCase();
      const email = (d.users?.email || '').toLowerCase();
      const license = (d.license_number || '').toLowerCase();
      const term = searchTerm.toLowerCase();
      return name.includes(term) || email.includes(term) || license.includes(term);
    });

    return [...list].sort((a, b) => {
      const sa = driverStates[a.driver_id] || {};
      const sb = driverStates[b.driver_id] || {};
      let va, vb;
      switch (sortField) {
        case 'hours_avail':
          va = sa.hours_available_sec ?? 999999;
          vb = sb.hours_available_sec ?? 999999;
          break;
        case 'must_finish':
          va = sa.must_finish_by ? new Date(sa.must_finish_by).getTime() : (sortDir === 'asc' ? Infinity : -Infinity);
          vb = sb.must_finish_by ? new Date(sb.must_finish_by).getTime() : (sortDir === 'asc' ? Infinity : -Infinity);
          break;
        case 'can_start':
          va = sa.can_start_at ? new Date(sa.can_start_at).getTime() : (sortDir === 'asc' ? -Infinity : Infinity);
          vb = sb.can_start_at ? new Date(sb.can_start_at).getTime() : (sortDir === 'asc' ? -Infinity : Infinity);
          break;
        case 'work_24h':
          va = sa.work_24h_seconds ?? 0;
          vb = sb.work_24h_seconds ?? 0;
          break;
        default:
          va = sa.hours_available_sec ?? 999999;
          vb = sb.hours_available_sec ?? 999999;
      }
      return sortDir === 'asc' ? va - vb : vb - va;
    });
  }, [drivers, searchTerm, driverStates, sortField, sortDir]);

  // Batch-load driver states
  const loadDriverStates = async () => {
    setStatesLoading(true);
    try {
      const data = await api('/admin/driver-states');
      setDriverStates(data.states || {});
    } catch {
      // Non-critical
    } finally {
      setStatesLoading(false);
    }
  };

  useEffect(() => {
    if (drivers.length > 0) loadDriverStates();
  }, [drivers.length]);

  // Invite driver with deep link
  const handleInvite = async (e) => {
    e.preventDefault();
    setLoading(true);
    setInviteResult(null);
    
    try {
      const result = await api('/auth/invite-driver-link', {
        method: 'POST',
        body: JSON.stringify(inviteForm)
      });
      
      setInviteResult(result);
      toast.success('Driver invited successfully!');
      onRefresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied!`);
    } catch {
      toast.error('Failed to copy');
    }
  };

  // Load driver detail with state
  const loadDriverDetail = async (driver) => {
    setShowDetail(driver);
    try {
      const state = await api(`/drivers/${driver.driver_id}/state`);
      setDriverState(state);
    } catch (err) {
      console.error('Failed to load driver state:', err);
      setDriverState(null);
    }
  };

  // Update driver
  const handleUpdateDriver = async (driverId) => {
    setLoading(true);
    try {
      // Update driver details
      await api(`/drivers/${driverId}`, {
        method: 'PUT',
        body: JSON.stringify({
          license_number: editForm.license_number,
          license_state: editForm.license_state,
          license_card_number: editForm.license_card_number,
          date_of_birth: editForm.date_of_birth,
          base_address: editForm.base_address
        })
      });
      
      toast.success('Driver updated');
      setShowEdit(null);
      onRefresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Open edit modal
  const openEditModal = (driver) => {
    setEditForm({
      license_number: driver.license_number || '',
      license_state: driver.license_state || 'NSW',
      license_card_number: driver.license_card_number || '',
      date_of_birth: driver.date_of_birth || '',
      base_address: driver.base_address || '',
      status: driver.status || 'ACTIVE'
    });
    setShowEdit(driver);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header with Search and Add */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-lg sm:text-xl font-semibold text-white">Drivers</h2>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search drivers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm w-full sm:w-64"
              data-testid="driver-search"
            />
          </div>
          <button
            onClick={() => { setShowInvite(true); setInviteResult(null); }}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition text-sm"
            data-testid="invite-driver-btn"
          >
            <Plus className="w-4 h-4" />
            Invite Driver
          </button>
          <button
            onClick={loadDriverStates}
            className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition"
            title="Refresh live statuses"
            data-testid="refresh-states-btn"
          >
            <RefreshCw className={`w-4 h-4 ${statesLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-slate-800/50 rounded-xl p-3 sm:p-4 border border-slate-700">
          <p className="text-slate-400 text-xs sm:text-sm">Total Drivers</p>
          <p className="text-xl sm:text-2xl font-bold text-white">{drivers.length}</p>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-3 sm:p-4 border border-slate-700">
          <p className="text-slate-400 text-xs sm:text-sm">Active</p>
          <p className="text-xl sm:text-2xl font-bold text-emerald-400">
            {drivers.filter(d => d.status === 'ACTIVE').length}
          </p>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-3 sm:p-4 border border-slate-700">
          <p className="text-slate-400 text-xs sm:text-sm">Inactive</p>
          <p className="text-xl sm:text-2xl font-bold text-slate-400">
            {drivers.filter(d => d.status !== 'ACTIVE').length}
          </p>
        </div>
      </div>

      {/* Invite Modal */}
      {showInvite && (
        <InviteDriverModal
          inviteForm={inviteForm} setInviteForm={setInviteForm}
          inviteResult={inviteResult} loading={loading}
          onSubmit={handleInvite} onCopy={copyToClipboard}
          onClose={() => {
            setShowInvite(false);
            setInviteForm({ first_name: '', last_name: '', email: '', license_number: '', license_state: 'NSW', license_card_number: '', date_of_birth: '', phone: '' });
            setInviteResult(null);
          }}
        />
      )}

      {/* Driver Detail Modal */}
      {showDetail && (
        <DriverDetailModal
          driver={showDetail} driverState={driverState}
          onEdit={() => { openEditModal(showDetail); setShowDetail(null); }}
          onClose={() => { setShowDetail(null); setDriverState(null); }}
        />
      )}

      {/* Edit Modal */}
      {showEdit && (
        <EditDriverModal
          driver={showEdit} editForm={editForm} setEditForm={setEditForm}
          loading={loading}
          onSave={() => handleUpdateDriver(showEdit.driver_id)}
          onClose={() => setShowEdit(null)}
        />
      )}

      {/* Drivers List */}
      <div className="bg-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden">
        {/* Mobile Card View */}
        <div className="block sm:hidden divide-y divide-slate-700">
          {filteredDrivers.length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-sm">
              {searchTerm ? 'No drivers match your search' : 'No drivers found'}
            </div>
          ) : (
            filteredDrivers.map(driver => (
              <DriverMobileCard
                key={driver.driver_id}
                driver={driver}
                driverStates={driverStates}
                onSelect={loadDriverDetail}
              />
            ))
          )}
        </div>

        {/* Desktop Table View */}
        <table className="w-full hidden sm:table">
          <thead className="bg-slate-700/50">
            <tr className="text-left text-slate-400 text-sm">
              <th className="px-4 py-3">Driver</th>
              <th className="px-4 py-3">Status</th>
              <AdminSortTh label="Hours Avail" field="hours_avail" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
              <AdminSortTh label="Must Finish" field="must_finish" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
              <AdminSortTh label="Can Start" field="can_start" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
              <AdminSortTh label="24h Work" field="work_24h" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="text-white">
            {filteredDrivers.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-slate-400">
                  {searchTerm ? 'No drivers match your search' : 'No drivers found. Click "Invite Driver" to add your first driver.'}
                </td>
              </tr>
            ) : (
              filteredDrivers.map(driver => (
                <DriverTableRow
                  key={driver.driver_id}
                  driver={driver}
                  driverState={driverStates[driver.driver_id]}
                  statesLoading={statesLoading}
                  onView={loadDriverDetail}
                  onEdit={openEditModal}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DriversPanel;
